import { isPrimitiveEntry, pageOwner, resolveProvidedPrimitive } from "@bloglovin/inspire-engine/Helpers/PrimitiveContextValue";

export const ENGINE_NAVIGATE_EVENT = "inspire:navigate";
export const APP_BUILDER_SECTION = "app";
export const APP_BUILDER_MOUNT_PREFIX = "/studio";
export const APP_BUILDER_INSPECTOR_COMPONENT = "RightInspector";
export const CREATE_AREA_HOME_SECTION = "home";
// Designer screens the host renders itself (task 134), keyed by their App.json layout name.
export const APP_BUILDER_CANVAS_SCREENS = Object.freeze({
  PageCanvasEditor: { kind: "page", context: "appPage" },
  ComponentCanvasEditor: { kind: "component", context: "appComponent" },
});
export const CREATE_AREA_APPS_SECTION = "apps";

export function createAreaRailSection(section) {
  if (!section) {
    return CREATE_AREA_HOME_SECTION;
  }
  return section === APP_BUILDER_SECTION ? CREATE_AREA_APPS_SECTION : section;
}

export const APP_BUILDER_SCREENS_WITH_INSPECTOR = [
  "EntityEditorPrimaryContent",
  "EventEditorPrimaryContent",
  "PermissionEditorPrimaryContent",
  "ScheduleEditorPrimaryContent",
  "DependencyEditorPrimaryContent",
];

export function appBuilderPagePath({ page, subPage, subPath }) {
  const head = [APP_BUILDER_SECTION, page, subPage].filter(Boolean).join("/");
  return subPath ? `${APP_BUILDER_MOUNT_PREFIX}/${head}/${subPath}` : `${APP_BUILDER_MOUNT_PREFIX}/${head}`;
}

export function appBuilderPageContexts(pageData, urlVars, hostContexts, pagePath) {
  const contexts = { ...hostContexts };
  for (const [contextVar, def] of Object.entries(pageData.providesContexts ?? {})) {
    if (isPrimitiveEntry(def)) {
      contexts[contextVar] = resolveProvidedPrimitive({
        owner: pageOwner(pagePath), contextName: contextVar, def, urlVars, allowUri: true,
      });
      continue;
    }
    if (def.entitySource !== "uri") {
      throw new Error(
        `App Builder page context "${contextVar}" uses entitySource "${def.entitySource}"; creator's Studio only resolves "uri" contexts.`,
      );
    }
    const pk = {};
    for (const [pkField, urlVar] of Object.entries(def.entityPkUrlVars ?? {})) {
      if (urlVars[urlVar] === undefined) {
        throw new Error(`App Builder page context "${contextVar}" needs URL var "${urlVar}", which the matched page path does not bind.`);
      }
      pk[pkField] = Number.isNaN(Number(urlVars[urlVar])) ? urlVars[urlVar] : Number(urlVars[urlVar]);
    }
    contexts[contextVar] = def.appName
      ? { entityType: def.entityType, pk, appName: def.appName }
      : { entityType: def.entityType, pk };
  }
  return contexts;
}

export const APP_BUILDER_ROW_COLOR_PROPERTIES = {
  "--app-builder-row-selected": "interactiveSelected",
  "--app-builder-row-hover": "interactiveHover",
};

export const APP_BUILDER_ROW_INK_PROPERTY = "--app-builder-row-ink";

export function appBuilderRowColors(themeBackgroundColors, themeFontColors) {
  const colors = {};
  for (const [property, alias] of Object.entries(APP_BUILDER_ROW_COLOR_PROPERTIES)) {
    const value = themeBackgroundColors?.[alias];
    if (!value) {
      throw new Error(`The Studio theme has no color.background.${alias}; App Builder sidebar and list rows need it for ${property}.`);
    }
    colors[property] = value;
  }
  const ink = themeFontColors?.onInteractive;
  if (!ink) {
    throw new Error(
      `The Studio theme has no color.font.onInteractive; hovered and selected App Builder rows need it for ${APP_BUILDER_ROW_INK_PROPERTY}.`,
    );
  }
  colors[APP_BUILDER_ROW_INK_PROPERTY] = ink;
  return colors;
}
